import { useCallback, useMemo, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { functionalUpdate, Updater } from "@tanstack/react-table";

import {
  ScheduleJobScheduleDto,
  ModelsControllerListSummariesData,
} from "@/client";
import { cn } from "@/lib/utils";
import { TimeSettings, useTimeSettings } from "@/state/time-settings";
import { TzPicker } from "@/routes/dashboard/-tz-picker";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { createPrefix } from "../table-view/search-namespace";

import {
  DialogPage,
  MultiStepDialogContent,
} from "../paginated-dialog-content/multi-step-dialog-content";
import { FormDatePickerItem } from "../form-inputs/date-picker";
import { ModelSelectionTableWithSelection } from "./model-selection-table-with-selection";
import {
  RunImmediatelyInput,
  ScheduleSpecificInput,
} from "./run-runImmediately-input";

export const ScheduledModelDeploymentSchema = z
  .object({
    modelId: z.number().optional(),
    modelName: z.string().optional(),
    runImmediately: z.boolean(),
    schedule: z.date().optional(),
    timezone: z
      .string()
      .min(1, { message: "Please select a timezone" })
      .optional(),
    includeCollaboratorDevices: z.boolean().optional(),
    // Added table filter fields which are used by ModelSelectionFilters
    // when combineForms={true} is used in ModelSelectionTable
    name: z.string().optional(),
    type: z.string().optional(),
    role: z.string().optional(),
  })
  .refine(
    (data) => {
      return data.modelId !== undefined;
    },
    {
      message: "Please select a model",
      path: ["modelId"],
    },
  )
  .refine(
    (data) => {
      if (data.runImmediately) {
        return true;
      }
      return data.schedule !== undefined;
    },
    {
      message: "Please select a schedule",
      path: ["schedule"],
    },
  )
  .refine(
    (data) => {
      if (data.runImmediately) {
        return true;
      }
      return data.timezone && data.timezone.length > 0;
    },
    {
      message: "Please select a timezone",
      path: ["timezone"],
    },
  )
  .refine(
    (data) => {
      if (data.runImmediately) {
        return true;
      }
      if (!data.schedule) {
        return true;
      }
      return data.schedule > new Date();
    },
    {
      message:
        'Cannot schedule in the past. Use "Run Immediately" to deploy now.',
      path: ["schedule"],
    },
  );

type ScheduledModelDeploymentFormValues = z.infer<
  typeof ScheduledModelDeploymentSchema
>;

interface ScheduledModelDeploymentProps {
  apiCallback: (
    modelId: number,
    schedule?: ScheduleJobScheduleDto,
    options?: { includeCollaboratorDevices?: boolean },
  ) => Promise<void | unknown>;
  onClose?: () => void;
  /**
   * Offer the in-site collaborator option. Only the cluster deployments honour it - a site or
   * device deployment targets the devices it was given, collaborators included.
   */
  showCollaboratorOption?: boolean;
}

const namespaced = createPrefix<ModelsControllerListSummariesData>()("smd_");

/**
 * Sits under the model table's filters row, above the table: the table fills the dialog, so
 * anything under it would need scrolling to reach.
 */
function CollaboratorOption() {
  const { t } = useTranslation();
  const form = useFormContext<ScheduledModelDeploymentFormValues>();
  const checked = form.watch("includeCollaboratorDevices") ?? false;

  return (
    <label className="flex w-fit cursor-pointer items-start gap-2">
      <Checkbox
        checked={checked}
        onCheckedChange={(value) =>
          form.setValue("includeCollaboratorDevices", value === true)
        }
      />
      <span className="flex flex-col">
        <span>{t("includeCollaboratorDevices")}</span>
        <span className="text-xs text-gray-500">
          {t("includeCollaboratorDevicesHint")}
        </span>
      </span>
    </label>
  );
}

export const ScheduledModelDeployment = ({
  apiCallback,
  onClose,
  showCollaboratorOption = false,
}: ScheduledModelDeploymentProps) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);

  const timeSettings = useTimeSettings();

  const form = useForm<ScheduledModelDeploymentFormValues>({
    resolver: zodResolver(ScheduledModelDeploymentSchema),
    defaultValues: {
      runImmediately: true,
      timezone: timeSettings.timeZone,
      schedule: new Date(),
      includeCollaboratorDevices: false,
    },
  });

  const runImmediately = form.watch("runImmediately");
  const includeCollaboratorDevices =
    form.watch("includeCollaboratorDevices") ?? false;
  const timezone = form.watch("timezone");
  const schedule = form.watch("schedule");
  const modelId = form.watch("modelId");
  const modelName = form.watch("modelName");

  const onSubmit = useCallback(
    async (data: ScheduledModelDeploymentFormValues) => {
      const scheduleDto: ScheduleJobScheduleDto | undefined =
        data.runImmediately
          ? undefined
          : {
              name: "Scheduled Model Deployment",
              startDate: data.schedule!,
              timezone: data.timezone!,
            };

      try {
        await apiCallback(data.modelId as number, scheduleDto, {
          includeCollaboratorDevices: data.includeCollaboratorDevices ?? false,
        });
        onClose?.();
      } catch (error) {
        console.error("Failed to deploy model:", error);
      }
    },
    [apiCallback, onClose],
  );

  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const queryArgument = useMemo(() => {
    const extracted = namespaced.extract(search);
    return {
      ...extracted,
      page: extracted.page ? Number(extracted.page) : 1,
      limit: extracted.limit ? Number(extracted.limit) : 10,
    } as ModelsControllerListSummariesData;
  }, [search]);

  const setQueryArgument = useCallback(
    (updater: Updater<ModelsControllerListSummariesData>) => {
      navigate({
        search: ((old: any) => ({
          ...old,
          ...namespaced.forward(functionalUpdate(updater, queryArgument)),
        })) as any,
      });
    },
    [navigate, queryArgument],
  );

  const Page1 = useMemo(
    () => (
      // The step is mounted with scroll: false, so this is a flex child of the dialog body and
      // sizes to it. Nothing here may scroll as a block: the table scrolls its own rows, which
      // keeps the filters and the collaborator option in view.
      <div className="space-y-4 flex flex-col flex-1 min-h-0 px-4 md:px-2">
        <h1 className="text-lg font-semibold flex-shrink-0">
          {t("selectModel")}
        </h1>
        <div className="flex flex-1 min-h-0 flex-col">
          <ModelSelectionTableWithSelection
            onSelect={(model) => {
              form.setValue("modelId", model.id, { shouldValidate: true });
              form.setValue("modelName", model.name, { shouldValidate: true });
            }}
            queryArgumentState={[queryArgument, setQueryArgument]}
            topExtra={
              showCollaboratorOption ? <CollaboratorOption /> : undefined
            }
          />
        </div>
      </div>
    ),
    [t, form, queryArgument, setQueryArgument, showCollaboratorOption],
  );

  const Page2 = useMemo(() => {
    return (
      <div className="space-y-4">
        <h1>{t("chooseWhenYouWantToDeployTheModel")}</h1>
        <FormField
          control={form.control}
          name="runImmediately"
          render={({ field }) => (
            <>
              <div
                className={cn("border p-2 rounded-md mb-2", {
                  "border-azure bg-azure/10": field.value,
                })}
              >
                <FormItem>
                  <FormControl>
                    <RunImmediatelyInput
                      {...field}
                      description="Deploy the model right away"
                    />
                  </FormControl>
                </FormItem>
              </div>

              <div
                className={cn("border p-2 rounded-md mb-2", {
                  "border-azure bg-azure/10": !field.value,
                })}
              >
                <FormItem>
                  <FormControl>
                    <ScheduleSpecificInput {...field} />
                  </FormControl>
                </FormItem>
                <div className="flex flex-col gap-2 ml-5 mt-4">
                  <FormDatePickerItem
                    {...timeSettings.as(timezone)}
                    name="schedule"
                    form={form}
                    label={t("date")}
                    button={{ disabled: field.value }}
                    container={{ className: field.value ? "opacity-50" : "" }}
                  />
                  <FormField
                    key={`timezone-${currentStep}`}
                    control={form.control}
                    name="timezone"
                    render={({ field: tzField }) => (
                      <FormItem className={field.value ? "opacity-50" : ""}>
                        <FormLabel>{t("timezone")}</FormLabel>
                        <FormControl>
                          <TzPicker
                            className="w-full"
                            value={tzField.value as TimeSettings["timezone"]}
                            onChange={(tz) => tzField.onChange(tz)}
                            isDisabled={field.value}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </>
          )}
        />
      </div>
    );
  }, [form, t, timezone, timeSettings, currentStep]);

  const Page3 = useMemo(() => {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-2">
          <div className="border p-2 rounded-md mb-2 bg-azure/5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div>{t("modelName")}</div>
                <div>{t("modelId")}</div>
                {showCollaboratorOption && (
                  <div>{t("inSiteCollaborators")}</div>
                )}
              </div>
              <div className="space-y-2">
                <div className="font-normal">{modelName || "-"}</div>
                <div className="font-normal">{modelId}</div>
                {showCollaboratorOption && (
                  <div className="font-normal">
                    {includeCollaboratorDevices
                      ? t("included")
                      : t("notIncluded")}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="border p-2 rounded-md mb-2 bg-azure/5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div>{t("deploymentType")}</div>
                {!runImmediately && (
                  <>
                    <div>{t("scheduledDate")}</div>
                    <div>{t("scheduledTime")}</div>
                    <div>{t("timezone")}</div>
                  </>
                )}
              </div>
              <div className="space-y-2">
                <div className="font-normal">
                  {runImmediately ? t("immediate") : t("scheduled")}
                </div>
                {!runImmediately && (
                  <>
                    <div className="font-normal">
                      {schedule?.toLocaleDateString()}
                    </div>
                    <div className="font-normal">
                      {schedule?.toLocaleTimeString()}
                    </div>
                    <div className="font-normal">{timezone}</div>
                  </>
                )}
              </div>
            </div>
          </div>
          {!runImmediately && (
            <div className="border p-2 rounded-md mb-2 border-azure bg-azure/10 text-azure font-bold">
              A Pending job will be created and the model will be deployed at
              the scheduled time.
            </div>
          )}
        </div>
      </div>
    );
  }, [
    t,
    runImmediately,
    timezone,
    schedule,
    modelId,
    modelName,
    showCollaboratorOption,
    includeCollaboratorDevices,
  ]);

  const pages: DialogPage[] = useMemo(
    () =>
      [
        {
          title: t("selectModel"),
          content: Page1,
          fields: ["modelId"],
          disableNext: modelId === undefined,
          scroll: false,
        },
        {
          title: t("scheduler"),
          content: Page2,
          fields: runImmediately ? [] : ["schedule", "timezone"],
        },
        {
          title: t("summary"),
          content: Page3,
        },
      ].filter(Boolean) as DialogPage[],
    [Page1, Page2, Page3, t, runImmediately, modelId],
  );

  const handleNext = useCallback(async () => {
    const fields = pages[currentStep].fields;

    // Validate without focusing
    const isStepValid = await form.trigger(
      fields as (keyof ScheduledModelDeploymentFormValues)[],
      { shouldFocus: false },
    );

    if (isStepValid) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, pages, form]);

  const handleBack = useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, []);

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => e.preventDefault()}
        className="flex flex-col flex-1 min-h-0"
      >
        <MultiStepDialogContent
          pages={pages}
          currentStep={currentStep}
          onNext={handleNext}
          onBack={handleBack}
          onSubmit={form.handleSubmit(onSubmit)}
          finishButtonText={t("deploy")}
          className="w-full flex-1 min-h-0"
        />
      </form>
    </Form>
  );
};
